import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  EmptyState,
  Input,
  Select,
  StatusPill,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  type Density,
  type Tone,
} from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import type { ProductDTO, ProductStatus } from "@wmart/shared-types";

interface ProductCounts {
  all: number;
  active: number;
  draft: number;
  archived: number;
}

interface ListResponse {
  items: ProductDTO[];
  nextCursor: string | null;
  counts: ProductCounts;
}

const STATUS_TONE: Record<ProductStatus, Tone> = {
  draft: "warning",
  active: "success",
  archived: "neutral",
};

export function ProductsList() {
  const navigate = useNavigate();
  const { notify, notifyProblem } = useToast();
  const [items, setItems] = useState<ProductDTO[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [counts, setCounts] = useState<ProductCounts | null>(null);
  const [statusFilter, setStatusFilter] = useState<ProductStatus | "all">("all");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [density, setDensity] = useState<Density>("default");

  // Density: read from localStorage on mount (Q5 carry-forward).
  useEffect(() => {
    const stored = window.localStorage.getItem("wmart.density");
    if (stored === "compact" || stored === "default" || stored === "cosy") {
      setDensity(stored);
    }
  }, []);

  // Debounce the free-text query.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  async function fetchPage(reset: boolean, cursor: string | null) {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "50");
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (debouncedQ.trim()) params.set("q", debouncedQ.trim());
      if (cursor) params.set("cursor", cursor);
      const data = await api.get<ListResponse>(
        `/v1/admin/products?${params.toString()}`,
      );
      setItems((prev) => (reset ? data.items : [...prev, ...data.items]));
      setNextCursor(data.nextCursor);
      setCounts(data.counts);
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to load products");
    } finally {
      setLoading(false);
    }
  }

  // Re-fetch on filter/search change.
  useEffect(() => {
    void fetchPage(true, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, debouncedQ]);

  async function archive(id: string) {
    if (
      !confirm("Archive this product? It will be hidden from the default list.")
    ) {
      return;
    }
    try {
      await api.delete(`/v1/admin/products/${id}`);
      notify("success", "Product archived");
      void fetchPage(true, null);
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to archive");
    }
  }

  const hasAnyFilter = statusFilter !== "all" || debouncedQ.trim() !== "";

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-display-sm text-ink-900">Products</h1>
        <div className="flex items-center gap-2">
          <Link to="/catalog/extractions/new">
            <Button variant="secondary">Import from supplier</Button>
          </Link>
          <Button onClick={() => navigate("/products/new")}>New product</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-72">
          <Input
            type="search"
            label="Search"
            placeholder="Search by title…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="w-48">
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as ProductStatus | "all")
            }
          >
            <option value="all">
              All (excl. archived){counts ? ` — ${counts.all}` : ""}
            </option>
            <option value="draft">
              Draft{counts ? ` — ${counts.draft}` : ""}
            </option>
            <option value="active">
              Active{counts ? ` — ${counts.active}` : ""}
            </option>
            <option value="archived">
              Archived{counts ? ` — ${counts.archived}` : ""}
            </option>
          </Select>
        </div>
        {hasAnyFilter ? (
          <Button
            variant="link"
            size="sm"
            onClick={() => {
              setStatusFilter("all");
              setQ("");
            }}
          >
            Clear
          </Button>
        ) : null}
      </div>

      {counts ? (
        <p className="text-body-sm text-ink-500">
          <span className="font-medium text-ink-700">{counts.all}</span> all
          {" · "}
          <span className="font-medium text-ink-700">{counts.active}</span>{" "}
          active{" · "}
          <span className="font-medium text-ink-700">{counts.draft}</span>{" "}
          draft{" · "}
          <span className="font-medium text-ink-700">{counts.archived}</span>{" "}
          archived
          {debouncedQ.trim() ? " (matching search)" : ""}
        </p>
      ) : null}

      {items.length === 0 && !loading ? (
        <EmptyState
          icon="package"
          title="No products yet"
          body="Adjust filters or create a new product to get started."
        />
      ) : (
        <>
          <Table density={density}>
            <THead>
              <TR>
                <TH>Slug</TH>
                <TH>Title</TH>
                <TH>Status</TH>
                <TH>Created</TH>
                <TH className="text-right">Actions</TH>
              </TR>
            </THead>
            <TBody>
              {items.map((p) => (
                <TR key={p.id}>
                  <TD className="font-mono text-body-sm text-ink-500">
                    {p.slug}
                  </TD>
                  <TD>
                    <Link
                      to={`/products/${p.id}`}
                      className="text-ink-900 hover:underline"
                    >
                      {p.title}
                    </Link>
                  </TD>
                  <TD>
                    <StatusPill tone={STATUS_TONE[p.status]}>
                      {p.status}
                    </StatusPill>
                  </TD>
                  <TD className="text-body-sm text-ink-500">
                    {p.created_at.slice(0, 10)}
                  </TD>
                  <TD className="text-right">
                    {p.status !== "archived" ? (
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => void archive(p.id)}
                        className="text-danger"
                      >
                        Archive
                      </Button>
                    ) : null}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>

          {nextCursor ? (
            <div className="flex justify-center">
              <Button
                variant="secondary"
                onClick={() => void fetchPage(false, nextCursor)}
                disabled={loading}
                loading={loading}
              >
                Load more
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
